/** Shared InMail credit formatting for campaign setup + leads UI. */

export type InMailCreditsBag = {
  classic: number | null;
  salesNavigator: number | null;
  recruiter?: number | null;
  error?: string | null;
} | null | undefined;

export type InMailCreditAccount = {
  status?: string;
  pending?: boolean;
  inMailCredits?: InMailCreditsBag;
};

export type InMailCreditSummary = {
  classic: number;
  salesNavigator: number;
  recruiter: number;
  accounts: number;
};

export function summarizeInMailCredits(
  accounts: InMailCreditAccount[],
): InMailCreditSummary | null {
  const connected = accounts.filter(a => a.status === 'connected' && !a.pending);
  if (!connected.length) return null;
  const classic = connected.reduce((sum, a) => sum + (a.inMailCredits?.classic ?? 0), 0);
  const salesNavigator = connected.reduce((sum, a) => sum + (a.inMailCredits?.salesNavigator ?? 0), 0);
  const recruiter = connected.reduce((sum, a) => sum + (a.inMailCredits?.recruiter ?? 0), 0);
  const anyKnown = connected.some(a =>
    a.inMailCredits
    && (
      a.inMailCredits.classic != null
      || a.inMailCredits.salesNavigator != null
      || a.inMailCredits.recruiter != null
    ),
  );
  if (!anyKnown) return null;
  return { classic, salesNavigator, recruiter, accounts: connected.length };
}

/**
 * The headline number plus which products it comes from.
 * e.g. { value: 12, detail: 'Sales Nav' } | { value: 12, detail: 'SN 12 · Recruiter 20 · classic 5' }
 */
export function splitInMailCreditBalance(summary: InMailCreditSummary): { value: number; detail: string } {
  const { classic, salesNavigator, recruiter } = summary;
  const parts = [
    { n: salesNavigator, short: 'SN', long: 'Sales Nav' },
    { n: recruiter, short: 'Recruiter', long: 'Recruiter' },
    { n: classic, short: 'classic', long: 'classic' },
  ].filter(p => p.n > 0);
  if (!parts.length) return { value: classic, detail: 'classic' };
  if (parts.length === 1) return { value: parts[0].n, detail: parts[0].long };
  return { value: parts[0].n, detail: parts.map(p => `${p.short} ${p.n}`).join(' · ') };
}

/** e.g. "12 (Sales Nav)" | "5 (classic)" | "SN 12 · Recruiter 20 · classic 5" */
export function formatInMailCreditBalance(summary: InMailCreditSummary): string {
  const { value, detail } = splitInMailCreditBalance(summary);
  return detail.includes(' · ') ? detail : `${value} (${detail})`;
}

export function formatInMailCreditsLabel(summary: InMailCreditSummary): string {
  return `InMail credits: ${formatInMailCreditBalance(summary)}`;
}

/**
 * Whether connected accounts have any paid InMail credits (classic + SN + Recruiter).
 * `null` = unknown (no connected account / balance not loaded yet).
 */
export function hasPaidInMailCredits(summary: InMailCreditSummary | null | undefined): boolean | null {
  if (!summary) return null;
  return (summary.classic + summary.salesNavigator + summary.recruiter) > 0;
}

/** Per-account line for the connected-account list; null when unknown. */
export function formatAccountInMailCreditsLine(credits: InMailCreditsBag): string | null {
  if (!credits) return null;
  if (credits.classic == null && credits.salesNavigator == null && credits.recruiter == null) return null;
  return formatInMailCreditsLabel({
    classic: credits.classic ?? 0,
    salesNavigator: credits.salesNavigator ?? 0,
    recruiter: credits.recruiter ?? 0,
    accounts: 1,
  });
}

const PRODUCT_LABELS: Record<string, string> = {
  premium: 'Premium',
  sales_navigator: 'Sales Navigator',
  recruiter: 'Recruiter',
};

/** Paid LinkedIn products on an account, e.g. "Premium · Recruiter"; null when none. */
export function formatAccountProducts(premiumFeatures: string[] | null | undefined): string | null {
  const labels = (premiumFeatures ?? [])
    .map(f => PRODUCT_LABELS[f])
    .filter(Boolean);
  return labels.length ? labels.join(' · ') : null;
}
